import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { StateBlock } from '@w7/ui'
import { useCmMe } from '@/api/hooks/useCmMe'
import { isCmIpAllowlistDenied } from '@/auth/cmIpAllowlist'
import { clearAuth, readAuth } from '@/auth/session'

export function AuthGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const auth = readAuth()
  const [denyReason, setDenyReason] = useState<'ip' | 'denied' | null>(null)
  const me = useCmMe()

  useEffect(() => {
    if (!me.isError) return
    let cancelled = false
    void (async () => {
      const ipDenied = await isCmIpAllowlistDenied()
      if (cancelled) return
      clearAuth()
      setDenyReason(ipDenied ? 'ip' : 'denied')
      navigate(ipDenied ? '/login?reason=ip' : '/login?reason=denied', { replace: true })
    })()
    return () => {
      cancelled = true
    }
  }, [me.isError, navigate])

  if (!auth) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  if (me.isLoading || (me.isError && !denyReason)) {
    return <StateBlock icon="activity" title="Checking CM access…" />
  }

  if (me.isError) {
    return (
      <Navigate
        to={denyReason === 'ip' ? '/login?reason=ip' : '/login?reason=denied'}
        replace
      />
    )
  }

  if (String(me.data?.caller.hierarchyLevel) !== '1') {
    const email = me.data?.caller.email
    const hierarchyLevel = me.data?.caller.hierarchyLevel
    // Keep tokens so NoAccess can end Keycloak SSO via id_token_hint.
    // Clearing here left the IdP session alive and trapped users on re-login.
    return (
      <Navigate
        to="/no-access"
        replace
        state={{ email, hierarchyLevel }}
      />
    )
  }

  return <>{children}</>
}
